import type { DataItem } from '@pn/core/domain/data';
import { hashFeatureId } from '@pn/core/domain/map';
import {
  generatePatternLayerId,
  getPointsLayerId,
  getSourceId,
} from '@pn/core/domain/workspace';
import type { MapboxIMap } from '@pn/services/map/mapbox';
import type { PolygonLikeFC } from '@pn/services/map/mapbox/custom/PolygonPatternCustomLayer';
import { customLayersRegistry } from '@pn/services/map/mapbox/custom/registry';
import { getCenterPoint } from '@pn/services/utils/geojson';
import { isNil } from 'lodash-es';

export const updateLayerData: MapboxIMap['updateLayerData'] = function (
  this: MapboxIMap,
  { layer, data }
) {
  if (!this.hasLayer(layer.id)) {
    throw new Error(`Layer ${layer.id} does not exist`);
  }

  const source = this._native.getSource(getSourceId(layer.id));
  if (source.type !== 'geojson') {
    throw new Error(`Layer ${layer.id} is not a GeoJSON layer`);
  }

  const featureCollection = generateGeoJsonFeatureCollection(data);

  source.setData(featureCollection);

  const patternLayerId = generatePatternLayerId(layer.id);
  if (customLayersRegistry.has(patternLayerId)) {
    const customLayer = customLayersRegistry.get(patternLayerId)!;
    customLayer.setData(featureCollection as PolygonLikeFC);
  }

  const pointsSource = this._native.getSource(
    getSourceId(getPointsLayerId(layer.id))
  );
  if (!isNil(pointsSource) && pointsSource.type === 'geojson') {
    const pointsFeatureCollection = generateGeoJsonFeatureCollection(
      data,
      true
    );
    pointsSource.setData(pointsFeatureCollection);
  }
};

/**
 * Each source data can be transformed into a points collection. This is useful
 * for rendering polygon features at lower zoom levels.
 */
function generateGeoJsonFeatureCollection(
  data: DataItem[],
  asPoints = false
): GeoJSON.FeatureCollection {
  const geojson: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [],
  };

  for (const item of data) {
    if (!isNil(item.geometry)) {
      let featureGeometry: GeoJSON.Geometry;

      if (asPoints) {
        featureGeometry = {
          type: 'Point',
          coordinates: getCenterPoint(item.geometry),
        };
      } else {
        featureGeometry = item.geometry;
      }

      geojson.features.push({
        type: 'Feature',
        id: hashFeatureId(item._id),
        geometry: featureGeometry,
        properties: item,
      });
    } else {
      console.error('Missing geometry from', item);
    }
  }

  return geojson;
}
